import { type AppCountry } from './AppCountry.ts';

export const SUPPORTED_LANGUAGES = {
    be: ['fr-BE', 'nl-BE'] as const,
    ch: ['fr-CH', 'de-CH', 'it-CH', 'en-CH'] as const,
    com: ['en-GB', 'fr-FR', 'de-DE', 'da-DK', 'nb-NO', 'pl-PL', 'sv-SE'] as const,
    de: 'de-DE' as const,
    es: 'es-ES' as const,
    fr: 'fr-FR' as const,
    it: 'it-IT' as const,
    lu: 'fr-BE' as const,
    nl: 'nl-NL' as const,
    pt: 'pt-PT' as const,
    uk: 'en-GB' as const
} satisfies Record<AppCountry, Readonly<string | string[]>>;

export const AUTOTRANSLATED_LANGUAGES = {
    be: [] as const,
    ch: [] as const,
    com: ['da-DK', 'nb-NO', 'pl-PL', 'sv-SE'] as const,
    de: [] as const,
    es: [] as const,
    fr: [] as const,
    it: [] as const,
    lu: [] as const,
    nl: [] as const,
    pt: [] as const,
    uk: [] as const
} satisfies Record<AppCountry, Readonly<string | string[]>>;

export type SupportedLanguages = typeof SUPPORTED_LANGUAGES;
export type AutoTranslatedLanguage = MergeAppLanguage<
    (typeof AUTOTRANSLATED_LANGUAGES)[keyof typeof AUTOTRANSLATED_LANGUAGES]
>;
export type AppLanguage = MergeAppLanguage<SupportedLanguages[keyof SupportedLanguages]>;

type CountrySupportedLanguages<C extends AppCountry> = MergeAppLanguage<(typeof SUPPORTED_LANGUAGES)[C]>;
type CountryAutoTranslatedLanguages<C extends AppCountry> = MergeAppLanguage<(typeof AUTOTRANSLATED_LANGUAGES)[C]>;

type NonAutoTranslatedLanguageByCountry<C extends AppCountry> = Exclude<
    CountrySupportedLanguages<C>,
    CountryAutoTranslatedLanguages<C>
>;

export type PerCountryLanguageMap<T> = {
    [C in AppCountry]: Record<NonAutoTranslatedLanguageByCountry<C>, T>;
};

// Typescript utils to merge all language keys from record above
type FlattenTuple<T> = T extends readonly [infer A, infer B] ? A | B : T;
export type MergeAppLanguage<T> = T extends string
    ? T
    : T extends readonly [infer A, ...infer Rest]
    ? FlattenTuple<A> | MergeAppLanguage<Rest>
    : never;
